import { type ReactNode, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { mdiFileUploadOutline } from "@mdi/js";
import Icon from "@mdi/react";
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  notification,
  Segmented,
  Select,
  Spin,
  Tooltip,
  Upload,
} from "antd";
import { observer } from "mobx-react-lite";

import {
  createAutopayment,
  requestInvoice,
  updateAutopayment,
} from "../../api/payment";
import { getSubscriptionConformance } from "../../api/subscription";
import { userStore } from "../../stores/userStore";
import { sendCounterEvent } from "../../utils/counterService";
import { createBeforeUpload } from "../../utils/createBeforeUpload";
import { formatDate } from "../../utils/date";
import { formatPriceInfo } from "../../utils/price";
import { usePendingAction } from "../../utils/usePendingAction";
import { SubscriptionConformanceModal } from "./SubscriptionConformanceModal";
import { SubscriptionEntityForm } from "./SubscriptionEntityForm";
import { SubscriptionPriceSummary } from "./SubscriptionPriceSummary";
import { SubscriptionPromoCode } from "./SubscriptionPromoCode";

import "./SubscriptionManageCheckout.css";

import type {
  PriceInfo,
  SubscriptionInAdvance,
  SubscriptionPlan,
  SubscriptionSettingsUpdate,
} from "../../api/subscription";
import type { PromoResult } from "./SubscriptionPromoCode";
import type { UploadFile } from "antd";

const { Dragger } = Upload;

const normFile = (event: UploadFile[] | { fileList: UploadFile[] }) => {
  if (Array.isArray(event)) return event;
  return event.fileList;
};

interface Props {
  selectedPlan: SubscriptionPlan;
  selectedPeriod: "monthly" | "yearly";
  inAdvance: SubscriptionInAdvance | null;
  settings: SubscriptionSettingsUpdate;
  configuredPrice: PriceInfo;
  configuredOriginalPrice: PriceInfo;
  configuration: ReactNode;
  onPaymentComplete?: (url: string) => void;
  refreshing?: boolean;
  isTrial?: boolean;
}

export const SubscriptionManageCheckout = observer(
  ({
    selectedPlan,
    selectedPeriod,
    inAdvance,
    settings,
    configuredPrice,
    configuredOriginalPrice,
    configuration,
    onPaymentComplete,
    refreshing = false,
    isTrial = false,
  }: Props) => {
    const { t, i18n } = useTranslation();
    const sub = userStore.subscription;
    const profile = userStore.profile;
    const [entityForm] = Form.useForm();
    const [paymentType, setPaymentType] = useState<"individual" | "entity">(
      "individual",
    );
    const { pending: processing, run: runFinish } = usePendingAction();
    const [promoResult, setPromoResult] = useState<PromoResult | null>(null);
    const [showComment, setShowComment] = useState(false);
    const [conformanceViolations, setConformanceViolations] = useState<
      string[] | null
    >(null);

    useEffect(() => {
      if (!profile || paymentType !== "entity") return;
      entityForm.setFieldsValue({
        company_name:
          (profile as unknown as Record<string, string>).company ?? "",
        contact_person:
          `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim(),
        contact_email: profile.email ?? "",
        contact_phone:
          (profile as unknown as Record<string, string>).phone ?? "",
      });
    }, [entityForm, paymentType, profile]);

    useEffect(() => {
      setPromoResult(null);
    }, [
      selectedPlan.id,
      selectedPeriod,
      settings.storage_limit,
      settings.user_limit,
    ]);

    const promoPrice =
      promoResult?.discountPercent != null ? promoResult.finalPrice : null;
    const finalPrice = isTrial
      ? { ...configuredPrice, amount: 0 }
      : (promoPrice ?? configuredPrice);
    const discountAmount = isTrial
      ? 0
      : Math.max(0, configuredOriginalPrice.amount - finalPrice.amount);
    const nextPaymentPrice =
      promoResult?.couponPermanent === true
        ? finalPrice
        : configuredOriginalPrice;
    const nextPaymentDate = inAdvance?.subscription.expire_at
      ? formatDate(inAdvance.subscription.expire_at, i18n.language).replaceAll(
          / /g,
          " ",
        )
      : null;
    const conversionDays = inAdvance?.convertion.additional_days ?? 0;
    const currentPlan = sub?.plan?.toLowerCase() ?? "lemon";
    const currentSettings = sub?.user_settings;
    const isSettingsChanging =
      (settings.user_limit != null &&
        settings.user_limit !== currentSettings?.user_limit) ||
      (settings.storage_limit != null &&
        settings.storage_limit !== currentSettings?.storage_limit);
    const isChanging =
      !sub?.autopayment ||
      currentPlan !== selectedPlan.id ||
      sub?.interval !== selectedPeriod ||
      isSettingsChanging;
    const isLegalEntityOrderDisabled = Boolean(
      (window as unknown as Record<string, unknown>).isLegalEntityOrderDisabled,
    );
    const showWireOption =
      i18n.language === "ru" &&
      userStore.isRuRegion &&
      profile?.country === "RU" &&
      !isLegalEntityOrderDisabled &&
      !isTrial &&
      !sub?.autopayment &&
      (selectedPlan.id === "pomelo" ||
        (selectedPlan.id === "mandarin" && selectedPeriod === "yearly"));
    const completePayment =
      onPaymentComplete ?? ((url: string) => (window.location.href = url));

    const currentUserLimit = sub?.user_settings?.user_limit ?? 0;
    const currentStorageLimit = sub?.user_settings?.storage_limit ?? 0;
    const planRank: Record<string, number> = {
      lemon: 0,
      mandarin: 1,
      pomelo: 2,
    };
    const isPlanDowngrade =
      (planRank[selectedPlan.id] ?? 0) < (planRank[currentPlan] ?? 0);
    const isPremiumLimitReduction =
      currentPlan === "pomelo" &&
      selectedPlan.id === "pomelo" &&
      ((settings.user_limit ?? 0) < currentUserLimit ||
        (settings.storage_limit ?? 0) < currentStorageLimit);
    const needsConformanceCheck =
      Boolean(userStore.webgis) && (isPlanDowngrade || isPremiumLimitReduction);

    useEffect(() => {
      if (!showWireOption) setPaymentType("individual");
    }, [showWireOption]);

    const handleFinish = async () => {
      if (refreshing || !isChanging) return;
      await runFinish(async () => {
        try {
          if (needsConformanceCheck) {
            const conformance = await getSubscriptionConformance({
              plan: selectedPlan.id,
              settings,
            });
            if (!conformance.conforms) {
              setConformanceViolations(conformance.violations);
              return;
            }
          }

          if (paymentType === "entity") {
            const entityValues = await entityForm.validateFields();
            const fileList = entityValues.files || [];
            const rawFiles = fileList
              .map((file: UploadFile) => file.originFileObj)
              .filter(Boolean);

            await requestInvoice({
              selectedPlan: selectedPlan.id,
              annualPayment: selectedPeriod === "yearly" ? "on" : "off",
              company_name: entityValues.company_name,
              contact_person: entityValues.contact_person,
              contact_email: entityValues.contact_email,
              contact_phone: entityValues.contact_phone,
              edo_operator: entityValues.edo_operator,
              comment: entityValues.comment,
              files: rawFiles,
              settings,
            });
            completePayment("/subscription?scenario=invoiceRequested");
            return;
          }

          const userData = {
            firstName: profile?.first_name ?? "",
            lastName: profile?.last_name ?? "",
            email: profile?.email ?? "",
            country: profile?.country ?? "",
          };

          if (sub?.autopayment) {
            sendCounterEvent("update-subscription");
            await updateAutopayment({
              plan: selectedPlan.id,
              interval: selectedPeriod,
              userData,
              coupon: promoResult?.coupon || undefined,
              settings,
            });
            completePayment(
              "/subscription?payment-attempt-status=success&scenario=updating",
            );
          } else {
            sendCounterEvent("buy-subscription");
            const { redirect_url } = await createAutopayment({
              provider: userData.country === "RU" ? "yookassa" : "stripe",
              plan: selectedPlan.id,
              interval: selectedPeriod,
              userData,
              coupon: promoResult?.coupon || undefined,
              trial: isTrial ? "on" : undefined,
              settings,
            });
            completePayment(redirect_url);
          }
        } catch (error) {
          if (error && typeof error === "object" && "errorFields" in error)
            return;
          notification.error({
            message: t("errors.shared.common_error"),
            placement: "topRight",
          });
        }
      });
    };

    const termsUrl = userStore.isRuRegion
      ? "https://nextgis.ru/terms-plans/"
      : "https://nextgis.com/terms-plans/";

    return (
      <div className="subscription-checkout subscription-manage-checkout">
        <SubscriptionConformanceModal
          open={conformanceViolations !== null}
          planName={selectedPlan.name}
          violations={conformanceViolations ?? []}
          onClose={() => setConformanceViolations(null)}
        />
        <div className="subscription-checkout__columns">
          <div className="subscription-checkout__left-wrapper">
            {configuration}
          </div>

          <div className="subscription-checkout__right">
            <Card
              styles={{
                body: {
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                },
              }}
            >
              {showWireOption && (
                <Segmented
                  block
                  size="large"
                  className="subscription-manage__payment-type"
                  value={paymentType}
                  onChange={(value) =>
                    setPaymentType(value as "individual" | "entity")
                  }
                  options={[
                    {
                      value: "individual",
                      label: t("subscription.payByCard"),
                    },
                    {
                      value: "entity",
                      label: t("subscription.payByWire"),
                    },
                  ]}
                />
              )}

              {paymentType !== "entity" &&
                (refreshing ? (
                  <div className="subscription-checkout__payment-info-loading">
                    <Spin />
                  </div>
                ) : (
                  <div className="subscription-checkout__payment-info">
                    <ul>
                      {!sub?.autopayment && (
                        <li>
                          {t(
                            isTrial
                              ? "subscription.weWillLinkCardTrial"
                              : "subscription.weWillLinkCard",
                          )}
                        </li>
                      )}
                      {sub?.autopayment &&
                        currentPlan !== "lemon" &&
                        isChanging &&
                        conversionDays > 0 && (
                          <li>
                            {t("subscription.convertionResult", {
                              plan: selectedPlan.name,
                              context: selectedPeriod,
                              days: t("subscription.days", {
                                count: conversionDays,
                              }),
                            })}
                          </li>
                        )}
                      {!sub?.autopayment &&
                        currentPlan !== "lemon" &&
                        conversionDays > 0 && (
                          <>
                            <li>
                              {t(
                                "subscription.weConvertRemainingBalanceNoAutopayments",
                              )}
                            </li>
                            <li>
                              {t("subscription.convertionResult", {
                                plan: selectedPlan.name,
                                context: selectedPeriod,
                                days: t("subscription.days", {
                                  count: conversionDays,
                                }),
                              })}
                            </li>
                          </>
                        )}
                      <li>
                        {t("subscription.nowWeWillCharge", {
                          price: formatPriceInfo(finalPrice),
                        })}
                      </li>
                      {nextPaymentDate && (
                        <li>
                          {isTrial ? (
                            t("subscription.afterTrialFirstPayment", {
                              price: formatPriceInfo(nextPaymentPrice),
                              date: nextPaymentDate,
                            })
                          ) : (
                            <>
                              {t("subscription.nextPaymentDetailed")}{" "}
                              {formatPriceInfo(nextPaymentPrice)}{" "}
                              {t("subscription.willBeScheduled", {
                                date: nextPaymentDate,
                              })}
                            </>
                          )}
                        </li>
                      )}
                      {!sub?.autopayment && (
                        <li>{t("subscription.cancelSubscriptionAnyTime")}</li>
                      )}
                    </ul>
                  </div>
                ))}

              {paymentType === "entity" && (
                <>
                  <SubscriptionEntityForm entityForm={entityForm} />
                  <Form form={entityForm} component={false} layout="vertical">
                    <Form.Item
                      name="files"
                      className="subscription-manage__company-upload"
                      rules={[
                        {
                          required: true,
                          message: t("subscription.companyCardAttachment"),
                        },
                      ]}
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                      style={{ marginBottom: 0 }}
                    >
                      <Dragger
                        multiple
                        aria-label={t("subscription.companyCardAttachment")}
                        beforeUpload={createBeforeUpload(t)}
                        accept=".jpg,.png,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                      >
                        <p className="subscription-manage__company-upload-label">
                          <span aria-hidden="true">*</span>
                          {t("subscription.companyCardAttachment")}
                        </p>
                        <Icon
                          className="subscription-manage__company-upload-icon"
                          path={mdiFileUploadOutline}
                          size={1}
                        />
                      </Dragger>
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(previous, current) =>
                        previous.use_edo !== current.use_edo
                      }
                    >
                      {({ getFieldValue }) => (
                        <div className="subscription-checkout__edo-row">
                          <Form.Item
                            name="use_edo"
                            valuePropName="checked"
                            noStyle
                          >
                            <Checkbox>{t("subscription.useEdo")}</Checkbox>
                          </Form.Item>
                          {getFieldValue("use_edo") && (
                            <Form.Item
                              name="edo_operator"
                              noStyle
                              rules={[
                                {
                                  required: true,
                                  message: t("support.fieldRequired"),
                                },
                              ]}
                            >
                              <Select
                                className="subscription-checkout__edo-select"
                                placeholder={t("subscription.selectEdo")}
                                options={[
                                  "edoDiadoc",
                                  "edoTensor",
                                  "edoKaluga",
                                  "edoTakskom",
                                  "edoOther",
                                ].map((key) => ({
                                  value: t(`subscription.${key}`),
                                  label: t(`subscription.${key}`),
                                }))}
                              />
                            </Form.Item>
                          )}
                        </div>
                      )}
                    </Form.Item>
                    {showComment ? (
                      <div className="subscription-manage__comment-expanded">
                        <div className="subscription-manage__comment-action">
                          <Button
                            type="link"
                            onClick={() => {
                              entityForm.resetFields(["comment"]);
                              setShowComment(false);
                            }}
                          >
                            {t("subscription.removeComment")}
                          </Button>
                        </div>
                        <Form.Item name="comment" noStyle>
                          <Input.TextArea
                            autoFocus
                            className="subscription-manage__comment-input"
                            rows={4}
                            placeholder={t("subscription.comment")}
                          />
                        </Form.Item>
                      </div>
                    ) : (
                      <div className="subscription-manage__comment-action">
                        <Button
                          type="link"
                          onClick={() => setShowComment(true)}
                        >
                          {t("subscription.addComment")}
                        </Button>
                      </div>
                    )}
                  </Form>
                  <SubscriptionPriceSummary
                    finalPrice={finalPrice}
                    originalPrice={configuredOriginalPrice}
                    discountAmount={discountAmount}
                    currency={finalPrice.currency}
                    nextPaymentDate={null}
                    nextPaymentPrice={null}
                    loading={refreshing}
                  />
                  <Button
                    type="primary"
                    block
                    size="large"
                    loading={processing || refreshing}
                    onClick={handleFinish}
                  >
                    {t("subscription.issueInvoice")}
                  </Button>
                </>
              )}
            </Card>

            {paymentType !== "entity" && (
              <>
                {!isTrial && (
                  <SubscriptionPromoCode
                    key={`${selectedPlan.id}:${selectedPeriod}:${settings.user_limit}:${settings.storage_limit}`}
                    selectedPlan={selectedPlan}
                    selectedPeriod={selectedPeriod}
                    settings={settings}
                    onPromoApply={setPromoResult}
                  />
                )}
                <SubscriptionPriceSummary
                  finalPrice={finalPrice}
                  originalPrice={isTrial ? null : configuredOriginalPrice}
                  discountAmount={discountAmount}
                  currency={finalPrice.currency}
                  nextPaymentDate={nextPaymentDate}
                  nextPaymentPrice={nextPaymentPrice}
                  loading={refreshing}
                />
                <div className="subscription-checkout__pay-actions">
                  <Tooltip
                    title={
                      isChanging
                        ? undefined
                        : t("subscription.noSubscriptionChanges")
                    }
                  >
                    <span className="subscription-manage__pay-button-tooltip">
                      <Button
                        type="primary"
                        block
                        size="large"
                        disabled={!isChanging || refreshing}
                        loading={processing || refreshing}
                        onClick={handleFinish}
                      >
                        {t(
                          isTrial
                            ? "subscription.trial.checkout.button"
                            : "subscription.pay",
                        )}
                      </Button>
                    </span>
                  </Tooltip>
                  <p className="subscription-checkout__agreement">
                    <Trans
                      i18nKey="subscription.agreement"
                      components={{
                        lnk: (
                          <a
                            href={termsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
);
